// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Loader2, UserCheck, UserPlus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ScoutConnectButtonProps {
  athleteId: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  className?: string;
}

export const ScoutConnectButton: React.FC<ScoutConnectButtonProps> = ({
  athleteId,
  variant = 'outline',
  size = 'sm',
  className = '',
}) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingConnection, setIsCheckingConnection] = useState(true);

  // Check if already connected
  useEffect(() => {
    const checkConnection = async () => {
      if (!profile?.id || !athleteId) return;

      try {
        const { data, error } = await supabase
          .from('scout_athlete_relationships')
          .select('id')
          .eq('scout_id', profile.id)
          .eq('athlete_id', athleteId)
          .eq('status', 'active')
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error checking scout connection:', error);
        } else {
          setIsConnected(!!data);
        }
      } catch (error) {
        console.error('Error checking scout connection:', error);
      } finally {
        setIsCheckingConnection(false);
      }
    };

    checkConnection();
  }, [profile?.id, athleteId]);

  const handleConnect = async () => {
    if (!profile?.id || !athleteId) return;

    setIsLoading(true);
    try {
      if (isConnected) {
        // Disconnect - set status to inactive
        const { error } = await supabase
          .from('scout_athlete_relationships')
          .update({ status: 'inactive' })
          .eq('scout_id', profile.id)
          .eq('athlete_id', athleteId);

        if (error) throw error;

        setIsConnected(false);
        toast({
          title: "Disconnected",
          description: "You have disconnected from this athlete.",
        });
      } else {
        // Connect - check if relationship exists but inactive
        const { data: existingRelationship, error: checkError } = await supabase
          .from('scout_athlete_relationships')
          .select('id')
          .eq('scout_id', profile.id)
          .eq('athlete_id', athleteId)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          throw checkError;
        }

        if (existingRelationship) {
          // Reactivate existing relationship
          const { error } = await supabase
            .from('scout_athlete_relationships')
            .update({ status: 'active' })
            .eq('id', existingRelationship.id);

          if (error) throw error;
        } else {
          // Create new relationship
          const { error } = await supabase
            .from('scout_athlete_relationships')
            .insert([{
              scout_id: profile.id,
              athlete_id: athleteId,
              status: 'active'
            }]);

          if (error) throw error;
        }

        setIsConnected(true);
        toast({
          title: "Connected",
          description: "You are now connected to this athlete.",
        });
      }
    } catch (error: any) {
      console.error('Error toggling scout connection:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update connection status.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isCheckingConnection) {
    return (
      <Button variant={variant} size={size} disabled className={className}>
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
        Checking...
      </Button>
    );
  }

  return (
    <Button
      variant={isConnected ? 'default' : variant}
      size={size}
      onClick={handleConnect}
      disabled={isLoading}
      className={className}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : isConnected ? (
        <UserCheck className="h-4 w-4 mr-2" />
      ) : (
        <UserPlus className="h-4 w-4 mr-2" />
      )}
      {isConnected ? 'Connected' : 'Connect'}
    </Button>
  );
};

export default ScoutConnectButton;